import type { LucideIcon } from "lucide-react";
import {
    Activity,
    BellRing,
    Bot,
    Brain,
    ClipboardList,
    Ear,
    FileText,
    GraduationCap,
    History,
    MessageCircle,
    MessageCircleQuestion,
    Monitor,
    NotebookPen,
    Pill,
    Stethoscope,
    UserRound,
    Users,
} from "lucide-react";
import {
    AimmaProductId,
    AimmaProductSuiteGroupId,
    isAimmaProductId,
} from "./healthTypes";
import {
    HealthLinkedCardTheme,
    healthProductSuiteIconGlyphFor,
    healthProductSuiteIconShellFor,
} from "./healthVisualStyles";

export type HealthProductSuiteIconSize = "group" | "product";

const GROUP_ICONS: Record<AimmaProductSuiteGroupId, LucideIcon> = {
    patient: UserRound,
    doctor: Stethoscope,
    nurse: Users,
    educator: GraduationCap,
    "nursing-robot": Bot,
};

const PRODUCT_ICONS: Record<AimmaProductId, LucideIcon> = {
    "aimma-diary": ClipboardList,
    "aimma-consultation": MessageCircle,
    "aimma-interview": UserRound,
    "aimma-chronicle": History,
    "aimma-listening": Ear,
    "aimma-triage": Activity,
    "aimma-diagnosis": Brain,
    "aimma-treatment": Pill,
    "aimma-reminder": BellRing,
    "aimma-reporting": FileText,
    "aimma-kiosk": MessageCircleQuestion,
    "aimma-bedside": Monitor,
    "aimma-notes": NotebookPen,
    "aimma-nursing-robot": Bot,
    "aimma-sensei": GraduationCap,
};

const SIZE_CLASSES: Record<
    HealthProductSuiteIconSize,
    { circle: string; glyph: string; stroke: number }
> = {
    group: {
        circle: "h-12 w-12",
        glyph: "h-[22px] w-[22px]",
        stroke: 1.75,
    },
    product: {
        circle: "h-9 w-9",
        glyph: "h-[18px] w-[18px]",
        stroke: 1.75,
    },
};

interface HealthProductSuiteIconProps {
    size: HealthProductSuiteIconSize;
    groupId?: AimmaProductSuiteGroupId;
    productId?: string;
    className?: string;
    muted?: boolean;
    /** Override group-based theme (doctor teal, nurse amber, patient sky, hub violet) */
    theme?: HealthLinkedCardTheme;
    /** Glyph only — no circular background or ring */
    bare?: boolean;
}

export function HealthProductSuiteIcon({
    size,
    groupId,
    productId,
    className = "",
    muted = false,
    theme,
    bare = false,
}: HealthProductSuiteIconProps) {
    let Icon: LucideIcon | undefined;

    if (productId && isAimmaProductId(productId)) {
        Icon = PRODUCT_ICONS[productId];
    } else if (groupId) {
        Icon = GROUP_ICONS[groupId];
    }

    if (!Icon) return null;

    const { circle, glyph, stroke } = SIZE_CLASSES[size];
    const resolvedTheme: HealthLinkedCardTheme = theme ?? "teal";

    if (bare) {
        return (
            <Icon
                className={`shrink-0 ${glyph} ${
                    muted
                        ? "text-grey-400"
                        : healthProductSuiteIconGlyphFor(resolvedTheme)
                } ${className}`}
                strokeWidth={stroke}
                aria-hidden
            />
        );
    }

    return (
        <span
            className={`inline-flex shrink-0 items-center justify-center rounded-full ${
                muted
                    ? "bg-grey-100/70"
                    : healthProductSuiteIconShellFor(resolvedTheme)
            } ${circle} ${className}`}
            aria-hidden
        >
            <Icon
                className={`${glyph} ${
                    muted
                        ? "text-grey-400"
                        : healthProductSuiteIconGlyphFor(resolvedTheme)
                }`}
                strokeWidth={stroke}
            />
        </span>
    );
}
